import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import OrderRow from '../components/OrderRow';
import QuestionnaireViewerModal from '../components/QuestionnaireViewerModal'; // New modal for questionnaire
import FileViewerModal from './FileViewerModal';
import RejectionModal from './RejectionModal'; // Import the new modal
import CompletionModal from './CompletionModal'; // Import the new completion modal

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 4rem auto;
  padding: 2rem;
  background-color: #f8f9fa;
  border-radius: 8px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 1rem;
`;

const Title = styled.h1`
  margin: 0;
`;

const LogoutButton = styled.button`
  padding: 10px 20px;
  background-color: #d9534f;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  margin-top: 2rem;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: #ffffff;
  
  th, td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #ddd;
    vertical-align: middle;
  }

  th {
    background-color: #f2f2f2;
    font-weight: 600;
  }

  tr:hover {
    background-color: #f5f5f5;
  }
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 2px solid #ccc;
  margin-bottom: 2rem;
`;

const TabButton = styled.button`
  padding: 10px 20px;
  font-size: 1.1rem;
  font-weight: 600;
  border: none;
  background-color: transparent;
  cursor: pointer;
  margin-bottom: -2px; /* Aligns with the container's border */
  color: ${props => props.$active ? '#005eb8' : '#555'};
  border-bottom: 3px solid ${props => props.$active ? '#005eb8' : 'transparent'};
`;

const SearchContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  padding: 10px;
  width: 100%;
  max-width: 300px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const DoctorDashboard = () => {
  const { user, logout, fetchOrdersByStatus, saveLetter, completeOrder, reopenOrder, regenerateDraft } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]); // This will now be the single source of truth
  const [loading, setLoading] = useState(true);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [reopeningOrderId, setReopeningOrderId] = useState(null);
  const [activeTab, setActiveTab] = useState('submitted');
  const [regeneratingOrderId, setRegeneratingOrderId] = useState(null);
  const [viewingFile, setViewingFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [rejectingOrder, setRejectingOrder] = useState(null); // State for rejection modal
  const [completingOrder, setCompletingOrder] = useState(null); // State for completion modal
  const [viewingQuestionnaire, setViewingQuestionnaire] = useState(null); // State for questionnaire modal


  const handleLogout = () => {
    logout();
    navigate('/doctor/login');
  }; // Semicolon added to terminate the function expression.

  const handleEditClick = (orderDetails) => {
    const orderId = orderDetails.orderId;
    if (editingOrderId === orderId) {
      setEditingOrderId(null);
    } else {
      setEditingOrderId(orderId);
    }
  };

  const handleDraftChange = (orderId, value) => {
    // Handle case where value is an event object (e.g. from onChange)
    const actualValue = (value && typeof value === 'object' && value.target) ? value.target.value : value;

    console.log(`[DEBUG] Draft changed for ${orderId}:`, actualValue);

    // Update the letterContent directly on the order in the local state
    setOrders(prevOrders => prevOrders.map(item =>
      item.orderDetails.orderId === orderId
        ? { ...item, orderDetails: { ...item.orderDetails, letterContent: actualValue } }
        : item
    ));
  };

  const handleSaveLetter = async (orderId, providedContent) => {
    // Determine content: use providedContent if it's a string, otherwise fallback to state.
    // This handles cases where OrderRow passes content directly OR where it relies on parent state.
    let draftContent = (typeof providedContent === 'string') ? providedContent : null;

    if (draftContent === null) {
      const orderToSave = orders.find(o => o.orderDetails.orderId === orderId);
      draftContent = orderToSave?.orderDetails?.letterContent || '';
    }

    console.log(`[DEBUG] Saving letter for ${orderId} with content:`, draftContent);

    try {
      const result = await saveLetter(orderId, draftContent);
      if (result.success) {
        toast.success('Letter saved successfully!');
        // Close the editor
        setEditingOrderId(null);
      } else {
        toast.error(result.error || 'Failed to save letter.');
      }
    } catch (error) {
      console.error('Save letter error:', error);
      toast.error('An error occurred while saving the letter.');
    }
  };

  const handleViewLetter = async (orderId) => {
    // The backend now generates and serves the PDF directly.
    // We construct the URL and open it in a new tab.
    const token = localStorage.getItem('doctorToken');
    const url = `${process.env.REACT_APP_BACKEND_URL}/doctors/orders/${orderId}/view-letter?token=${token}`;
    window.open(url, '_blank');
  };

  const handleViewTranscript = async (orderId) => {
    try {
      const token = localStorage.getItem('doctorToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/doctors/orders/${orderId}/view-transcript?token=${token}`);
      const data = await response.json();
      if (data.success) {
        setViewingFile({ type: 'text', content: data.transcript, title: 'AI Audio Transcript' });
      } else { toast.error(data.error); }
    } catch (e) { toast.error("Failed to load transcript."); }
  };

  const handleConfirmCompletion = async (orderId, clinicalNotes) => {
    setCompletingOrder(null); // Close modal
    toast.info('Completing order...');
    try {
      const result = await completeOrder(orderId);
      if (result.success) {
        toast.success('Order completed successfully!');
        // Remove the completed order from the list
        setOrders(prevOrders => prevOrders.filter(item => item.orderDetails.orderId !== orderId));
      } else {
        toast.error(result.error || 'Failed to complete order.');
      }
    } catch (error) {
      toast.error('An error occurred while completing the order.');
    }
  };

  const handleReopenOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to reopen this order? This will move it back to the "Submitted" queue for revision.')) {
      return;
    }

    setReopeningOrderId(orderId);
    try {
      const result = await reopenOrder(orderId);
      if (result.success) {
        toast.success('Order has been reopened.');
        // Remove the order from the current (completed) list
        setOrders(prevOrders => prevOrders.filter(item => item.orderDetails.orderId !== orderId));
      } else {
        toast.error(result.error || 'Failed to reopen order.');
      }
    } catch (error) {
      toast.error('An error occurred while reopening the order.');
    } finally {
      setReopeningOrderId(null);
    }
  };

  const handleRegenerateDraft = async (orderId) => {
    if (!window.confirm('Are you sure you want to regenerate the AI draft? The current text in the editor will be replaced.')) {
      return;
    }
    setRegeneratingOrderId(orderId);
    toast.info('Regenerating AI draft, please wait...');
    try {
      const result = await regenerateDraft(orderId);
      if (result.success) {
        toast.success('AI draft has been regenerated.');
        // Update the local state for the specific order with the new draft
        handleDraftChange(orderId, result.newDraft);
      } else {
        toast.error(result.error || 'Failed to regenerate draft.');
      }
    } catch (error) {
      toast.error('An error occurred while regenerating the draft.');
    } finally {
      setRegeneratingOrderId(null);
    }
  };

  const handleRejectSubmit = async (orderId, reason) => {
    setRejectingOrder(null); // Close modal immediately
    toast.info('Processing rejection...');
    try {
      const token = localStorage.getItem('doctorToken');
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/doctors/orders/${orderId}/reject`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success('Order rejected successfully.');
        setOrders(prev => prev.filter(o => o.orderDetails.orderId !== orderId));
      } else {
        toast.error(res.data.error || 'Failed to reject order.');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'An error occurred.');
    }
  };

  const handleViewQuestionnaire = (orderId, answers, questionnaireFiles) => {
    setViewingQuestionnaire({ orderId, answers, questionnaireFiles });
  };


  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const data = await fetchOrdersByStatus(activeTab);
        if (data.success) {
          // This is the key fix: We need to ensure the local state `orders`
          // is populated with the full order data, including `letterContent`.
          // The previous code was not explicitly setting the state with the fetched data.
          // By setting it here, the `draft` prop in `OrderRow` will receive the correct
          // content from the database on initial load and after a refresh.
          setOrders(data.orders);
        } else {
          setOrders([]);
          toast.error(data.error || 'Failed to fetch orders.');
        }
      } catch (error) {
        console.error("Error fetching orders in DoctorDashboard:", error); // Added console.error for debugging
        setOrders([]);
        toast.error('An error occurred while fetching orders.');
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [fetchOrdersByStatus, activeTab]);

  const handlers = {
    onEdit: handleEditClick,
    onViewLetter: handleViewLetter,
    onComplete: (order) => setCompletingOrder(order), // Open the completion modal
    onReopen: handleReopenOrder,
    onViewFile: setViewingFile,
    onDraftChange: handleDraftChange,
    onViewTranscript: handleViewTranscript,
    onSave: handleSaveLetter,
    onRegenerate: handleRegenerateDraft, // Pass the new handler
    onReject: (order) => setRejectingOrder(order), // Handler to open the modal
    onViewQuestionnaire: handleViewQuestionnaire,
  };

  const filteredOrders = orders.filter(order => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const name = (order.userName || '').toLowerCase();
    const dob = (order.userDob || '').toLowerCase();
    const phone = (order.userPhone || '').toLowerCase();
    const address = order.userAddress ? Object.values(order.userAddress).filter(Boolean).join(' ').toLowerCase() : '';
    const orderDate = order.orderDetails.date ? new Date(order.orderDetails.date).toLocaleDateString().toLowerCase() : '';
    const orderId = (order.orderDetails.orderId || '').toLowerCase();
    return name.includes(term) || dob.includes(term) || phone.includes(term) || address.includes(term) || orderDate.includes(term) || orderId.includes(term);
  });

  return (
    <DashboardContainer>
      <Header>
        <Title>Hello, {user?.name || 'Doctor'}!</Title>
        <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
      </Header>

      <TabContainer>
        <TabButton $active={activeTab === 'submitted'} onClick={() => setActiveTab('submitted')}>
          Submitted for Review
        </TabButton>
        <TabButton $active={activeTab === 'completed'} onClick={() => setActiveTab('completed')}>
          Completed
        </TabButton>
      </TabContainer>

      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Search name, DOB, date..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </SearchContainer>

      <TableWrapper>
        {loading ? <p>Loading {activeTab} orders...</p> : (
          <StyledTable>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer Name</th>
                <th>DOB</th>
                <th>Address</th>
                <th>Mobile Number</th>
                <th>Order Date</th>
                <th>ID</th>
                <th>Photo Evidence</th>
                <th>SCR</th>
                <th>Questionnaire</th>
                <th>Supporting Documents</th>
                <th>AI Data</th>
                <th>Video Confirmation</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length > 0 ? filteredOrders.map((order) => {
                // Clone the order to avoid mutating state directly
                const enrichedOrder = { ...order, orderDetails: { ...order.orderDetails } };

                // Ensure completed orders have the correct view link, overriding any DB placeholders like "SECURE_BLOB"
                if (enrichedOrder.orderDetails.status === 'completed') {
                  const token = localStorage.getItem('doctorToken');
                  // Construct the direct link to the backend endpoint which handles the redirect to SAS URL
                  enrichedOrder.orderDetails.letterUrl = `${process.env.REACT_APP_BACKEND_URL}/doctors/orders/${enrichedOrder.orderDetails.orderId}/view-letter?token=${token}`;
                }

                // Helper to inject placeholder URL and metadata so OrderRow displays the button
                const enrichUploads = (uploads) => {
                  if (!uploads) return;
                  const token = localStorage.getItem('doctorToken');
                  Object.keys(uploads).forEach(type => {
                    const file = uploads[type];
                    if (file && file.filename) {
                      // Construct the direct link to the backend endpoint with the doctor token for authentication
                      file.url = `${process.env.REACT_APP_BACKEND_URL}/doctors/orders/${enrichedOrder.orderDetails.orderId}/view-upload/${type}?token=${token}`;
                      file.orderId = order.orderDetails.orderId;
                      file.uploadType = type;
                    }
                  });
                };

                // Enrich both potential locations of uploads
                if (enrichedOrder.uploads) enrichUploads(enrichedOrder.uploads);
                if (enrichedOrder.orderDetails.uploads) enrichUploads(enrichedOrder.orderDetails.uploads);
                // Files attached to a questionnaire question (e.g. an HIV test result)
                // use the same view-upload endpoint, keyed by the question's field name.
                if (enrichedOrder.questionnaireFiles) enrichUploads(enrichedOrder.questionnaireFiles);
                if (enrichedOrder.orderDetails.questionnaireFiles) enrichUploads(enrichedOrder.orderDetails.questionnaireFiles);

                // Determine if the AI is currently processing this order
                // This should only apply when viewing the 'submitted' tab.
                const isAiProcessing = activeTab === 'submitted' && order.orderDetails.status === 'submitted';

                return (
                  <OrderRow
                    key={order.orderDetails.orderId}
                    order={enrichedOrder}
                    activeTab={activeTab}
                    editingOrderId={editingOrderId}
                    completingOrderId={completingOrder?.orderDetails.orderId}
                    reopeningOrderId={reopeningOrderId}
                    regeneratingOrderId={regeneratingOrderId}
                    draft={order.orderDetails.letterContent || ''}
                    transcript={order.orderDetails.aiTranscript || ''}
                    isSaving={false} // This state is now managed within OrderRow if needed, or globally if multiple can save at once.
                    isAiProcessing={isAiProcessing}
                    handlers={handlers}
                  />
                )
              }) : (
                <tr>
                  <td colSpan="12" style={{ textAlign: 'center' }}>No {activeTab} orders found.</td>
                </tr>
              )}
            </tbody>
          </StyledTable>
        )}
      </TableWrapper>
      {viewingFile && <FileViewerModal file={viewingFile} onClose={() => setViewingFile(null)} />}
      {rejectingOrder && <RejectionModal orderId={rejectingOrder.orderId} onSubmit={handleRejectSubmit} onClose={() => setRejectingOrder(null)} />}
      {completingOrder && <CompletionModal order={completingOrder} onSubmit={handleConfirmCompletion} onClose={() => setCompletingOrder(null)} />}
      {viewingQuestionnaire && <QuestionnaireViewerModal data={viewingQuestionnaire} onClose={() => setViewingQuestionnaire(null)} onViewFile={setViewingFile} />}
    </DashboardContainer>
  );
};

export default DoctorDashboard;